import { adminBffFetch } from '@avila-roffe/api-clients-admin-bff';
import type { Tool, ToolScope, ToolStatus } from './types.js';

function toRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

function normalizeTool(raw: unknown): Tool {
  const r = toRecord(raw);
  const scope = (
    ['PUBLIC', 'APPLICATION', 'TENANT'].includes(r.scope as string) ? r.scope : 'APPLICATION'
  ) as ToolScope;
  const status = (
    ['ACTIVE', 'DRAFT', 'DEPRECATED'].includes(r.status as string) ? r.status : 'DRAFT'
  ) as ToolStatus;
  return {
    id: String(r.id ?? ''),
    appSlug: String(r.appSlug ?? ''),
    name: String(r.name ?? ''),
    tenantSlug: typeof r.tenantSlug === 'string' ? r.tenantSlug : null,
    gitPath: String(r.gitPath ?? ''),
    gitSha: typeof r.gitSha === 'string' ? r.gitSha : null,
    version: String(r.version ?? '1.0.0'),
    scope,
    status,
    description: String(r.description ?? ''),
    jsonSchema: toRecord(r.jsonSchema),
    interface: toRecord(r.interface),
    isEnabled: r.isEnabled !== false,
    tags: Array.isArray(r.tags) ? (r.tags as string[]) : [],
    createdBy: String(r.createdBy ?? ''),
    updatedBy: String(r.updatedBy ?? ''),
    createdAt: String(r.createdAt ?? ''),
    updatedAt: String(r.updatedAt ?? ''),
  };
}

export async function listTools(limit = 50): Promise<Tool[]> {
  const res = await adminBffFetch<{ data?: { data?: unknown[] } }>(`/api/v1/tools?limit=${limit}`, {
    method: 'GET',
  });
  const items = (res as { data?: { data?: unknown[] } }).data?.data ?? [];
  return items.map(normalizeTool);
}

export async function getToolById(id: string): Promise<Tool> {
  const res = await adminBffFetch<{ data?: unknown }>(`/api/v1/tools/${id}`, { method: 'GET' });
  return normalizeTool((res as { data?: unknown }).data ?? res);
}

export async function createTool(payload: {
  name: string;
  appSlug?: string;
  description?: string;
  scope?: ToolScope;
  status?: ToolStatus;
  jsonSchema?: Record<string, unknown>;
  isEnabled?: boolean;
  tags?: string[];
  createdBy?: string;
}): Promise<Tool> {
  const res = await adminBffFetch<{ data?: unknown }>('/api/v1/tools', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return normalizeTool((res as { data?: unknown }).data ?? res);
}

export async function updateTool(
  id: string,
  payload: {
    name?: string;
    description?: string;
    scope?: ToolScope;
    status?: ToolStatus;
    jsonSchema?: Record<string, unknown>;
    isEnabled?: boolean;
    tags?: string[];
    updatedBy?: string;
  },
): Promise<Tool> {
  const res = await adminBffFetch<{ data?: unknown }>(`/api/v1/tools/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  return normalizeTool((res as { data?: unknown }).data ?? res);
}

export async function deleteTool(id: string): Promise<void> {
  await adminBffFetch(`/api/v1/tools/${id}`, { method: 'DELETE' });
}
